import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Modal, Button, Form } from 'react-bootstrap';
import PropTypes from 'prop-types';
import AwpTreeSelect from '../Common/AwpTreeSelect';
import { FormSelect } from '@components/Common/AwpFormInputs';
import { selectAllHazardForecastDropDown } from '@awp/stores/hazardForecastSlice';
import { getAvalancheGeographicAreas } from '@awp/stores/avalancheSlice';
import { Crud } from '@awp/shared/Crud';
// import './ForecastRecipient.css';

const getHazardAreas = (arr, areaIds = [], parentChecked = false) => {
  if (!arr || arr.length === 0) return arr;
  const hasAreaIds = areaIds && areaIds.length > 0;
  const result = [];
  for (const obj of arr) {
    const checked = (areaIds && areaIds.includes(obj.value)) || parentChecked;
    const newObj = {
      ...obj,
      checked,
      expanded:
        checked ||
        parentChecked ||
        (obj.treeLevel === 0 && hasAreaIds) ||
        (obj.treeLevel === 1 && obj.children && obj.children.some((x) => areaIds.includes(x.value))),
    };

    if (newObj.children && newObj.children.length) {
      newObj.children = getHazardAreas(newObj.children, areaIds, checked);
    }
    result.push(newObj);
  }
  return result;
};

const ConditionEditModal = ({ showConditionEditModal, setShowConditionEditModal, setFieldValue, setTouched }) => {
  const close = () => setShowConditionEditModal({ show: false });
  const avalancheAreasRaw = useSelector((state) => selectAllHazardForecastDropDown(state));
  const avalanchePrograms = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE PROGRAM'));
  const [areaIds, setAreaIds] = useState([]);
  const [areaList, setAreaList] = useState(null);
  const [program, setProgram] = useState(null);

  const crud = new Crud();

  useEffect(() => {
    const areaIds = showConditionEditModal.data?.AvalancheAreaIds;
    setAreaIds(areaIds);
    const avalancheAreas = getHazardAreas(avalancheAreasRaw, areaIds);
    setAreaList(avalancheAreas);
    setFieldValue('program', null);
  }, [showConditionEditModal.data?.AvalancheAreaIds]);

  const getChildrenIds = (parentId) => {
    const childrenIds = [];

    const search = (area) => {
      if (area.value === parentId) {
        if (area.children) area.children.forEach((child) => childrenIds.push(child.value));
      }
      if (area.children) area.children.forEach((child) => search(child));
    };

    avalancheAreasRaw.forEach((area) => search(area));
    return childrenIds;
  };

  const handleAreaSelect = (currentNode, selectedNodes) => {
    setTouched({ [showConditionEditModal.data.Id]: true });
    let ids = currentNode.checked
      ? [...new Set([...areaIds, currentNode.value])]
      : [...new Set(areaIds.filter((x) => x !== currentNode.value))];

    if (currentNode.treeLevel === 1) {
      if (currentNode.checked) {
        const childrenIds = getChildrenIds(currentNode.value);
        ids = ids.filter((id) => !childrenIds.includes(id));
      }
    }

    if (currentNode.treeLevel === 2) {
      const parent1 = avalancheAreasRaw[0].children.find((x) => x.value === currentNode.parentId);
      ids = handleLevel2(parent1, ids, currentNode, selectedNodes);

      const parent2 = avalancheAreasRaw[1].children.find((x) => x.value === currentNode.parentId);
      ids = handleLevel2(parent2, ids, currentNode, selectedNodes);
    }

    setAreaIds(ids);
    updateTreeSelectData(program, ids);
  };

  const handleLevel2 = (parent, ids, currentNode, selectedNodes) => {
    if (parent) {
      const allChecked = !parent.children.some((x) => !ids.includes(x.value));
      if (allChecked) {
        parent.children.forEach((x) => {
          ids = ids.filter((id) => id !== x.value);
        });
        if (!ids.includes(parent.value)) {
          ids = [...ids, parent.value];
        }
      } else {
        ids = ids.filter((id) => id !== parent.value);
      }
      if (!currentNode.checked) {
        const children = selectedNodes.filter((x) => x.parentId === parent.value);
        children.forEach((x) => {
          if (!ids.includes(x.value)) {
            ids = [...ids, x.value];
          }
        });
      }
    }
    return ids;
  };

  //warning: this is a mutation!
  const filterObjectsByParentId = (data, programId) => {
    const filteredList = data.filter((item) => {
      if (item.treeLevel === 1 && item.parentId !== programId) {
        return false;
      }
      if (item.children && item.children.length > 0) {
        item.children = filterObjectsByParentId(item.children, programId);
      }
      return true;
    });

    return filteredList;
  };

  const programOnChange = (opt, areaIds) => {
    setProgram(opt);
    updateTreeSelectData(opt, areaIds);
  };

  const updateTreeSelectData = (program, areaIds) => {
    const avalancheAreas = getHazardAreas(avalancheAreasRaw, areaIds);
    if (program) {
      setAreaList(filterObjectsByParentId(avalancheAreas, program.value));
    } else {
      setAreaList(avalancheAreas);
    }
  };

  const getAreaInfo = (areaIds) => {
    const programIdsSet = new Set();
    const labelSet = new Set();

    const search = (area) => {
      if (areaIds.includes(area.value)) {
        programIdsSet.add(area.programId);
        labelSet.add(area.label);
      }

      if (area.children) area.children.forEach((child) => search(child));
    };

    avalancheAreasRaw.forEach((area) => search(area));
    return { programIds: Array.from(programIdsSet), labels: Array.from(labelSet) };
  };

  const addClickHandler = () => {
    const info = getAreaInfo(areaIds);
    const value = {
      ...showConditionEditModal.data,
      AvalancheAreaIds: areaIds,
      AvalancheAreas: info.labels.join(' ,'),
      ProgramIds: info.programIds,
    };
    setFieldValue(showConditionEditModal.data.Id, value).then(() => close());
  };

  return (
    <Modal
      show={showConditionEditModal.show}
      onHide={close}
      backdrop="static"
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header>{crud.getModalTitle({ title: showConditionEditModal.data?.Distribution })}</Modal.Header>
      <Modal.Body>
        <FormSelect
          className="avalanche-select"
          readOnly={false}
          name="program"
          id="program"
          label="Program"
          options={avalanchePrograms}
          isMulti={false}
          postChange={(e) => {
            programOnChange(e, areaIds);
          }}
          isClearable={true}
        />
        <Form.Group className="mt-3 forecastRecipient">
          <AwpTreeSelect
            data={areaList}
            showDropdown="always"
            texts={{ placeholder: 'Search' }}
            keepTreeOnSearch={true}
            keepChildrenOnSearch
            showPartiallySelected={true}
            inlineSearchInput={true}
            keepOpenOnSelect={true}
            mode="multiSelect"
            onChange={handleAreaSelect}
            label="Area"
            disablePoppingOnBackspace={true}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={close}>
          Cancel
        </Button>
        <Button variant="primary" onClick={addClickHandler}>
          Add
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

ConditionEditModal.propTypes = {
  showConditionEditModal: PropTypes.object,
  setShowConditionEditModal: PropTypes.func,
  setFieldValue: PropTypes.func,
  setTouched: PropTypes.func,
};

export default ConditionEditModal;
